import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Table } from "antd";
import { gray100, gray700, gray800, gray900 } from "core/resources/theme/colors";
import { RdoTableRow } from "./rdoOperacoes.types";
import { rdoOperacoesColumns } from "./rdoOperacoesColumns";
import RdoDetalheOperacaoModal from "./RdoDetalheOperacaoModal";

type Props = {
  rows: RdoTableRow[];
  ocupacao: string;
  periodo: string;
  safraPeriodoId: string;
  filtersKey: string;
  expandedRowKeys: string[];
  onExpandedRowKeysChange: (keys: string[]) => void;
};

/** Uma tabela só: filhos entram como linhas irmãs (protótipo / Opção A). */
function flattenVisibleRows(
  rows: RdoTableRow[],
  expandedRowKeys: string[]
): RdoTableRow[] {
  const out: RdoTableRow[] = [];
  for (const row of rows) {
    out.push(row);
    if (
      row.isParent &&
      expandedRowKeys.includes(row.key) &&
      row.detalhamentos?.length
    ) {
      out.push(...row.detalhamentos);
    }
  }
  return out;
}

function RdoOperacoesTable({
  rows,
  ocupacao,
  periodo,
  safraPeriodoId,
  filtersKey,
  expandedRowKeys,
  onExpandedRowKeysChange,
}: Props) {
  const [selectedRow, setSelectedRow] = useState<RdoTableRow | null>(null);
  const originKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setSelectedRow(null);
  }, [filtersKey]);

  const closeModal = useCallback(() => {
    const key = originKeyRef.current;
    setSelectedRow(null);
    requestAnimationFrame(() => {
      if (!key) return;
      const el = document.querySelector(
        `tr[data-row-key="${key}"]`
      ) as HTMLElement | null;
      if (el) {
        el.setAttribute("tabindex", "-1");
        el.focus();
      }
    });
  }, []);

  const onToggleExpand = useCallback(
    (rowKey: string) => {
      if (expandedRowKeys.includes(rowKey)) {
        onExpandedRowKeysChange(expandedRowKeys.filter((k) => k !== rowKey));
      } else {
        onExpandedRowKeysChange([...expandedRowKeys, rowKey]);
      }
    },
    [expandedRowKeys, onExpandedRowKeysChange]
  );

  const columns = useMemo(
    () => rdoOperacoesColumns({ expandedRowKeys, onToggleExpand }),
    [expandedRowKeys, onToggleExpand]
  );

  const dataSource = useMemo(
    () => flattenVisibleRows(rows, expandedRowKeys),
    [rows, expandedRowKeys]
  );

  return (
    <TableScroll>
      <StyledTable
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        tableLayout="fixed"
        rowClassName={(record) =>
          record.isParent ? "rdo-row-parent" : "rdo-row-child"
        }
        onRow={(record) => ({
          onClick: () => {
            originKeyRef.current = record.key;
            setSelectedRow(record);
          },
          style: { cursor: "pointer" },
        })}
      />
      <RdoDetalheOperacaoModal
        row={selectedRow}
        ocupacao={ocupacao}
        periodo={periodo}
        safraPeriodoId={safraPeriodoId}
        onClose={closeModal}
      />
    </TableScroll>
  );
}

export default RdoOperacoesTable;

const TableScroll = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  min-width: 0;
`;

const StyledTable = styled(Table)`
  .ant-table {
    font-size: 12px;
    color: ${gray900};
  }

  .ant-table-container table {
    min-width: 1400px;
  }

  .ant-table-thead > tr > th {
    position: static !important;
    white-space: nowrap;
    background: ${gray100};
    font-weight: 700;
    color: ${gray900};
  }

  .ant-table-header,
  .ant-table-body {
    position: static !important;
    overflow: visible !important;
  }

  .ant-table-tbody > tr.rdo-row-parent > td {
    font-weight: 700;
    color: ${gray900};
    background: #fafbfc;
  }

  .ant-table-tbody > tr.rdo-row-child > td {
    font-weight: 500;
    color: ${gray800};
    background: #fff;
  }

  .ant-table-tbody > tr.rdo-row-child > td:first-child {
    color: ${gray700};
  }

  .ant-table-tbody > tr:hover > td {
    background: #f0f7f3 !important;
  }
` as typeof Table;
